
import SourceProductInformation from 'SourceComponent/ProductInformation/ProductInformation.component';
import PropTypes from 'prop-types';

import { ProductType } from 'Type/ProductList';

import ProductDescription from 'Component/ProductDescription';
import ProductCare from 'Component/ProductCare';
import ProductFaq from 'Component/ProductFaq';
import ProductIngredients from 'Component/ProductIngredients';
import ProductReviews from 'Component/ProductReviews';
import ProductInfoTabs from 'Component/ProductInfoTabs';
import ProductRelated from 'Component/ProductRelated';

export default class ProductInformation extends SourceProductInformation {
    static propTypes = {
        product: ProductType.isRequired,
        areDetailsLoaded: PropTypes.bool.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            topSection: null,
        };

        this.description = React.createRef();
        this.ingredients = React.createRef();
        this.care = React.createRef();
        this.faq = React.createRef();
        this.reviews = React.createRef();
        this.related = React.createRef();

        this.scrollToContent = this.scrollToContent.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll() {
        const sectionNames = this.getSectionNames();
        const sections = this.getSectionRefs(sectionNames);

        let elements = [];
        let topElement;
        for (const section of sections) {
            if (!section.current) {
                break;
            }
            const element = section.current.getBoundingClientRect().top;
            elements = [...elements, element];
        }
        elements.sort();
        // eslint-disable-next-line no-magic-numbers
        const filteredElement = Math.min(...elements.filter(number => number > 0 && number < 50));
        if (filteredElement !== Infinity) {
            topElement = sections.filter(
                section => section.current.getBoundingClientRect().top === filteredElement
            )[0];
        } else {
            topElement = sections.filter(
                section => section.current && section.current.getBoundingClientRect().bottom > 200
            )[0];
        }
        const topElementIndex = sections.indexOf(topElement);
        const topSection = sectionNames[topElementIndex];
        if (topSection) {
            this.setState({ topSection });
        }
    }

    getSectionRefs(sectionNames) {
        const newSections = [];
        sectionNames.map((section) => {
            if (section === __('Description')) {
                newSections.push(this.description);
            }
            if (section === __('Ingredients')) {
                newSections.push(this.ingredients);
            }
            if (section === __('Total care')) {
                newSections.push(this.care);
            }
            if (section === __('Questions and answers')) {
                newSections.push(this.faq);
            }
            if (section === __('Reviews')) {
                newSections.push(this.reviews);
            }
            if (section === __('Product related')) {
                newSections.push(this.related);
            }
        });
        newSections.filter(section => section !== undefined);
        return newSections;
    }

    getSectionNames() {
        let sectionNames = [
            __('Description'),
            __('Ingredients'),
            __('Total care'),
            __('Questions and answers'),
            __('Reviews'),
            __('Product related')
        ];
        const {
            code,
            product: {
                description: { html } = { },
                attributes: {
                    adresabilitate,
                    mod_utilizare,
                    beneficii,
                    ingredients,
                    ingrediente_active
                } = {},
                maintenance_scheme,
                product_links
            } = {}
        } = this.props;

        if (html === ''  && !adresabilitate && !mod_utilizare && !beneficii) {
            sectionNames = sectionNames.filter(section => section !== __('Description'));
        }

        if (!(ingrediente_active && ingrediente_active.attribute_value ) && !(ingredients && ingredients.attribute_value )) {
            sectionNames = sectionNames.filter(section => section !== __('Ingredients'));
        }

        if (code === 'en') {
            sectionNames = sectionNames.filter(section => section !== __('Questions and answers'));
        }

        if (maintenance_scheme && JSON.parse(maintenance_scheme).length === 0) {
            sectionNames = sectionNames.filter(section => section !== __('Total care'));
        }

        if (!product_links || !product_links.length) {
            sectionNames = sectionNames.filter(section => section !== __('Product related'));
        }

        return sectionNames;
    }

    scrollToContent(content) {
        const options = {
            behavior: 'smooth',
            block: 'start'
        };

        switch (content) {
        case __('Description'):
            this.scrollToTargetAdjusted(this.description);
            break;
        case __('Ingredients'):
            this.scrollToTargetAdjusted(this.ingredients);
            break;
        case __('Total care'):
            this.scrollToTargetAdjusted(this.care);
            break;
        case __('Questions and answers'):
            this.faq.current.scrollIntoView(options);
            break;
        case __('Reviews'):
            this.scrollToTargetAdjusted(this.reviews);
            break;
        case __('Product related'):
            this.scrollToTargetAdjusted(this.related);
            break;
        default:
            this.description.current.scrollIntoView(options);
            break;
        }
    }

    scrollToTargetAdjusted(section) {
        const element = section;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.current.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        // eslint-disable-next-line no-magic-numbers
        const offsetPosition = elementPosition - 68;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }

    getSections() {
        const sectionNames = this.getSectionNames();
        const buffer = [];
        sectionNames.map(section => buffer.push(<div key={ section } label={ section } />));

        return buffer;
    }

    render() {
        const { topSection } = this.state;
        const {
            code,
            product,
            product: {
                description: { html } = { },
                attributes: {
                    adresabilitate,
                    mod_utilizare,
                    beneficii,
                    ingredients,
                    ingrediente_active
                } = { },
                maintenance_scheme,
                product_links
            },
            areDetailsLoaded
        } = this.props;

        const tab = topSection || __('Description');

        if (!areDetailsLoaded) {
            return null;
        }

        return (
            <>
                <ProductInfoTabs scrollToContent={ this.scrollToContent } tab={ tab }>
                    { this.getSections() }
                </ProductInfoTabs>
                <div block="ProductSection" elem="Wrapper">
                    { ( html || adresabilitate || mod_utilizare || beneficii ) && (
                        <div block="ProductSection" ref={ this.description }>
                            <ProductDescription
                                description={ html }
                                addressability={ adresabilitate }
                                instructions={ mod_utilizare }
                                benefits={ beneficii }
                            />
                        </div>
                    ) }
                    { ( ( ingrediente_active &&  ingrediente_active.attribute_value) || (ingredients && ingredients.attribute_value ) ) && (
                        <div block="ProductSection" ref={ this.ingredients }>
                            <ProductIngredients ingredients={ ingredients } active_ingredients={ ingrediente_active } />
                        </div>
                    ) }
                    { maintenance_scheme && maintenance_scheme !== '[]' && (
                        <div block="ProductSection" ref={ this.care }>
                            <ProductCare relatedProducts={ JSON.parse(maintenance_scheme) } areDetailsLoaded={ areDetailsLoaded } />
                        </div>
                    ) }
                    { code !== 'en' && (
                        <div block="ProductSection" ref={ this.faq }>
                            <ProductFaq product={ product } areDetailsLoaded={ areDetailsLoaded } />
                        </div>
                    ) }

                    <div id="product-review" block="ProductSection" ref={ this.reviews }>
                        <ProductReviews product={ product } areDetailsLoaded={ areDetailsLoaded } />
                    </div>
                    { product_links.length !== 0 && (
                        <div block="ProductSection" ref={ this.related }>
                            <ProductRelated areDetailsLoaded={ areDetailsLoaded } />
                        </div>
                    ) }
                </div>
            </>
        );
    }
}
